import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase, Database } from '../lib/supabase'

type Profile = Database['public']['Tables']['profiles']['Row']

interface AuthContextType {
  user: User | null
  profile: Profile | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: any }>
  signOut: () => Promise<{ error: any }>
  resetPassword: (email: string) => Promise<{ error: any }>
  signInWithGoogle: () => Promise<{ error: any }>
  signInWithApple: () => Promise<{ error: any }>
  clearAuthCache: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check if Supabase client is available
    if (!supabase) {
      setLoading(false)
      return
    }

    // Clear potentially corrupted auth data on initialization
    const clearStaleAuth = () => {
      try {
        // Clear all Supabase auth-related items from localStorage
        const keys = Object.keys(localStorage)
        const supabaseKeys = keys.filter(key =>
          key.startsWith('supabase.auth') ||
          key.startsWith('sb-') ||
          key.includes('supabase')
        )

        // Check for expired tokens and clear all if any are expired
        let shouldClearAll = false

        supabaseKeys.forEach(key => {
          try {
            const item = localStorage.getItem(key)
            if (item) {
              const parsed = JSON.parse(item)
              // Check for expired tokens in various formats
              if (parsed.expires_at && new Date(parsed.expires_at * 1000) < new Date()) {
                shouldClearAll = true
              }
              if (parsed.access_token && parsed.expires_at && new Date(parsed.expires_at * 1000) < new Date()) {
                shouldClearAll = true
              }
            }
          } catch {
            shouldClearAll = true
          }
        })

        if (shouldClearAll) {
          supabaseKeys.forEach(key => {
            localStorage.removeItem(key)
          })

          // Also clear session storage
          const sessionKeys = Object.keys(sessionStorage)
          const supabaseSessionKeys = sessionKeys.filter(key =>
            key.startsWith('supabase.auth') ||
            key.startsWith('sb-') ||
            key.includes('supabase')
          )
          supabaseSessionKeys.forEach(key => {
            sessionStorage.removeItem(key)
          })
        }
      } catch {
        // If we can't parse, just clear everything Supabase-related
        const keys = Object.keys(localStorage)
        keys.filter(key => key.includes('supabase') || key.startsWith('sb-')).forEach(key => {
          localStorage.removeItem(key)
        })
      }
    }

    clearStaleAuth()

    // Get initial session with better error handling and timeout
    const initializeAuth = async () => {
      try {
        // Increase timeout to 30 seconds for better reliability
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Session check timeout')), 30000)
        )

        const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise]) as any

        if (error) {
          // Don't set loading to false here - let the auth listener handle it
          return
        }

        setSession(session)
        setUser(session?.user ?? null)

        if (session?.user) {
          await fetchProfile(session.user.id)
        }
        setLoading(false)
      } catch (error) {
        // Don't clear session data on timeout - the auth listener will handle authentication
        if (error instanceof Error && error.message === 'Session check timeout') {
          // Don't set loading to false - let the auth listener do it
          // The auth listener has already fired with SIGNED_IN status
        } else {
          // Clear any stale session data only for other errors
          setSession(null)
          setUser(null)
          setProfile(null)
          setLoading(false)
        }
      }
    }

    initializeAuth()

    // Listen for auth changes with error handling
    let subscription: any = null
    try {
      const authListener = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
        try {
          setSession(session)
          setUser(session?.user ?? null)

          if (session?.user) {
            await fetchProfile(session.user.id)
          } else {
            setProfile(null)
          }
          setLoading(false)
        } catch {
          setLoading(false)
        }
      })
      subscription = authListener.data.subscription
    } catch {
      setLoading(false)
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe()
      }
    }
  }, [])

  const fetchProfile = async (userId: string) => {
    if (!supabase) {
      return
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error && error.code !== 'PGRST116') {
        return
      }

      setProfile(data)
    } catch {
      // Profile fetch failed silently - user can still use the app
    }
  }

  const signIn = async (email: string, password: string) => {
    if (!supabase) {
      return { error: new Error('Authentication service is not available. Please check your configuration.') }
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      return { error }
    } catch {
      return { error: new Error('Authentication service error. Please try again.') }
    }
  }

  const signUp = async (email: string, password: string, displayName?: string) => {
    if (!supabase) {
      return { error: new Error('Authentication service is not available. Please check your configuration.') }
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      })

      if (!error && data.user) {
        // Create profile - errors here don't block signup
        await supabase
          .from('profiles')
          .insert({
            id: data.user.id,
            email: data.user.email!,
            display_name: displayName || null,
            role: 'user'
          })
      }

      return { error }
    } catch {
      return { error: new Error('Authentication service error. Please try again.') }
    }
  }

  const signOut = async () => {
    if (!supabase) {
      return { error: new Error('Authentication service is not available.') }
    }

    try {
      const { error } = await supabase.auth.signOut()
      return { error }
    } catch {
      return { error: new Error('Sign out failed. Please try again.') }
    }
  }

  const resetPassword = async (email: string) => {
    if (!supabase) {
      return { error: new Error('Authentication service is not available. Please check your configuration.') }
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      })
      return { error }
    } catch {
      return { error: new Error('Password reset failed. Please try again.') }
    }
  }

  const signInWithGoogle = async () => {
    if (!supabase) {
      return { error: new Error('Authentication service is not available. Please check your configuration.') }
    }

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })
      return { error }
    } catch {
      return { error: new Error('Google sign-in failed. Please try again.') }
    }
  }

  const signInWithApple = async () => {
    if (!supabase) {
      return { error: new Error('Authentication service is not available. Please check your configuration.') }
    }

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })
      return { error }
    } catch {
      return { error: new Error('Apple sign-in failed. Please try again.') }
    }
  }

  const clearAuthCache = useCallback(() => {
    // Clear localStorage
    const localKeys = Object.keys(localStorage)
    const supabaseLocalKeys = localKeys.filter(key =>
      key.startsWith('supabase.auth') ||
      key.startsWith('sb-') ||
      key.includes('supabase')
    )

    supabaseLocalKeys.forEach(key => {
      localStorage.removeItem(key)
    })

    // Clear sessionStorage
    const sessionKeys = Object.keys(sessionStorage)
    const supabaseSessionKeys = sessionKeys.filter(key =>
      key.startsWith('supabase.auth') ||
      key.startsWith('sb-') ||
      key.includes('supabase')
    )

    supabaseSessionKeys.forEach(key => {
      sessionStorage.removeItem(key)
    })

    // Reset state
    setSession(null)
    setUser(null)
    setProfile(null)
    setLoading(false)

    // Reload the page after a short delay
    setTimeout(() => {
      window.location.reload()
    }, 1000)
  }, [setSession, setUser, setProfile, setLoading])

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        resetPassword,
        signInWithGoogle,
        signInWithApple,
        clearAuthCache,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}